import React, { useEffect, useState } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import "../Pages/CreateEditPage.css";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import FileUploadComponent from "./FileUploadComponent.tsx";
import { Contratti, Referenti } from "../lib/interface.ts";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../Redux/Store/index.ts";
import { allGet, allPatch, allPost, allPut } from "../Redux/Actions.tsx";
import { urlcommittenti, urlcontratti } from "../lib/api.ts";
import { Checkbox } from "primereact/checkbox";
import DeleteModal from "./DeleteModal.tsx";
import { Dropdown } from "primereact/dropdown";
import { format } from "date-fns";
import { InputNumber } from "primereact/inputnumber";
// RIMOSSO: import { Calendar } from "primereact/calendar";
// RIMOSSO: import { addLocale, locale as setLocale } from "primereact/api";

import { useAlert } from "./Alert.tsx";

// Nuovi import per react-datepicker
import DatePicker, { registerLocale } from "react-datepicker";
import { it } from "date-fns/locale";
import "react-datepicker/dist/react-datepicker.css";

// Registra la localizzazione italiana
registerLocale("it", it);

interface FormContrattiProps {
  action: "crea" | "modifica";
  type: any;
  id?: any;
}

const FormContratti: React.FC<FormContrattiProps> = ({ action, type, id }) => {
  const [contratto, setContratto] = useState<Contratti>({
    id: "",
    codice: "",
    descrizione: "",
    dataInizio: "",
    dataFine: "",
    importo: 0,
    sconto: 0,
    metodoPagamento: "",
    attoAggiuntivo: false,
    dataAttoAggiuntivo: "",
    scadenzaAttoAggiuntivo: "",
    importoAttoAggiuntivo: 0,
    emissionePolizze: "",
    scadenzaPolizze: "",
    acquisizioneAti: false,
    nomeAzienda: "",
    percentualeAcquisizioneAti: 0,
    documentoPrincipale: "",
    committenteId: "",
    note: "",
    referenti: [],
    eventi: [],
    cantieri: [],
    task: [],
  });

  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const { token } = useSelector((state: RootState) => state.login);
  const [isEditing, setIsEditing] = useState<boolean>(!id);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // Funzione per formattare la data in "yyyy-MM-dd" prima di salvarla
  const dateToString = (date: Date) => format(date, "yyyy-MM-dd");

  // Stato per gestire i referenti
  const [arrayReferentiCommittente, setArrayReferentiCommittente] = useState<Referenti[]>([]);

  // Stato per i file associati al contratto
  const [filesContratto, setFilesContratto] = useState<any[]>([]);

  // Per i dropdown "Committente" e "Metodo di Pagamento"
  const [searchText, setSearchText] = useState("");
  const [paymentSearchText, setPaymentSearchText] = useState("");
  const committenti = useSelector((state: RootState) => state.committenti.committenti || []);
  const metodiDiPagamento = useSelector((state: RootState) => state.metodi.metodi);

  // Stato per eventuali errori di validazione
  const [errors, setErrors] = useState({
    codice: false,
  });

  const showAlert = useAlert();

  // Stato per gestire il documento principale del contratto
  const [principalFileName, setPrincipalFileName] = useState("");
  const [principalFileUrl, setPrincipalFileUrl] = useState("");
  const [isLoadingPrincipalFile, setIsLoadingPrincipalFile] = useState(true);

  // Validazione dei campi obbligatori
  const validateFields = () => {
    const newErrors = {
      codice: !contratto.codice.trim(),
    };

    setErrors(newErrors);
    return !Object.values(newErrors).some((error) => error);
  };

  // Se in modifica, carica i dati del contratto
  useEffect(() => {
    if (action === "modifica" && id) {
      const url = `${urlcontratti}/${id}`;
      dispatch(allGet(url, token)).then((singoloContratto) => {
        if (singoloContratto) {
          setContratto(singoloContratto);
        } else {
          console.error("Errore nel caricamento del contratto");
        }
      });
    }
  }, [action, id, dispatch, token]);

  // Quando cambia il committenteId, carica i suoi referenti
  useEffect(() => {
    if (contratto.committenteId) {
      handleGetReferenti(contratto.committenteId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contratto.committenteId]);

  // Aggiorna il documento principale se cambia la lista dei file
  useEffect(() => {
    if (filesContratto.length > 0 && contratto.documentoPrincipale) {
      const principalFile = filesContratto.find((f) => f.id === contratto.documentoPrincipale);
      if (principalFile) {
        setPrincipalFileName(principalFile.fileKey.split("/").pop() || "");
        setPrincipalFileUrl(principalFile.url || "");
      } else {
        setPrincipalFileName("");
        setPrincipalFileUrl("");
      }
    } else {
      setPrincipalFileName("");
      setPrincipalFileUrl("");
    }
  }, [filesContratto, contratto.documentoPrincipale]);

  // Se è disponibile principalFileName, segnala che ha finito il loading
  useEffect(() => {
    if (principalFileName !== null) {
      setIsLoadingPrincipalFile(false);
    }
  }, [principalFileName]);

  // Funzione per caricare i referenti del committente selezionato
  const handleGetReferenti = (id: string) => {
    const urlcommittenteDettaglio = `${urlcommittenti}/${id}`;
    dispatch(allGet(urlcommittenteDettaglio, token)).then((singoloCommittente) => {
      if (singoloCommittente) {
        setArrayReferentiCommittente(singoloCommittente.referenti);
      } else {
        console.error("Errore nel caricamento del committente");
      }
    });
  };

  // Gestore generico per i cambi di valore
  const handleChange = (field: keyof Contratti, value: any) => {
    setContratto((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Salvataggio (creazione o modifica)
  const handleSubmit = () => {
    if (!validateFields()) {
      console.error("Errore: campi obbligatori non compilati.");
      return;
    }

    if (action === "crea") {
      dispatch(allPost(urlcontratti, token, contratto, showAlert)).then((idContratto) => {
        if (idContratto) {
          showAlert("success", "Creato", "Contratto creato con successo.", "bottom-end", 3000);
        } else {
          console.error("Creazione del contratto fallita");
        }
      });
    } else if (action === "modifica" && id) {
      const url = `${urlcontratti}/${id}`;
      const urlReferenti = `${urlcontratti}/${id}/referenti`;

      dispatch(allPut(url, token, contratto)).then((updatedContratto) => {
        if (updatedContratto) {
          console.log("Contratto aggiornato:", updatedContratto);
          showAlert("success", "Salvato", "Contratto aggiornato con successo.", "bottom-end", 3000);
          setIsEditing(false);
        } else {
          console.error("Modifica del contratto fallita");
        }
      });

      // Aggiorna la lista dei referenti associati
      dispatch(allPatch(urlReferenti, token, contratto.referenti));
    }
    handleClose();
  };

  // Entrare in modalità "Modifica"
  const handleEdit = () => {
    setIsEditing(true);
  };

  // Torna alla lista
  const handleClose = () => {
    navigate("/contratti");
  };

  // Chiude la modale di eliminazione
  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
  };

  // Gestisce il toggle (aggiunta/rimozione) dei referenti selezionati
  const toggleReferente = (nuovoReferente: Referenti) => {
    setContratto((prev) => {
      const isSelected = prev.referenti.some((ref) => ref.id === nuovoReferente.id);
      if (isSelected) {
        return {
          ...prev,
          referenti: prev.referenti.filter((ref) => ref.id !== nuovoReferente.id),
        };
      } else {
        return {
          ...prev,
          referenti: [...prev.referenti, nuovoReferente],
        };
      }
    });
  };

  return (
    <div className="home-container">
      <div className="d-flex flex-column flex-lg-row justify-content-between align-items-center mb-5">
        <h2 className="titolo-header-Crea-Edit">
          Contratti/
          <b>{action === "crea" ? "Nuovo Contratto" : contratto.codice}</b>
        </h2>
        <div className="d-flex">
          {isEditing ? (
            <Button className="ms-2 button d-flex px-3" onClick={handleSubmit}>
              <i className="pi pi-save me-2"></i>
              <p className="p-0 m-0">Salva</p>
            </Button>
          ) : (
            <Button className="ms-2 button d-flex px-3" onClick={handleEdit}>
              <i className="pi pi-pencil me-2"></i>
              <p className="p-0 m-0">Modifica</p>
            </Button>
          )}
          {action === "modifica" && (
            <Button onClick={() => setIsDeleteModalOpen(true)} className="ms-2 button d-flex px-3">
              <i className="pi pi-trash me-2"></i>
              <p className="p-0 m-0">Elimina</p>
            </Button>
          )}

          <Button onClick={handleClose} className="ms-2 button d-flex px-3">
            <i className="pi pi-times me-2"></i>
            <p className="p-0 m-0">Chiudi</p>
          </Button>
        </div>
      </div>

      <Row className="g-3">
        <Col xs={12} className="px-0">
          <div className="create-edit-top-contratti">
            <Row className="row-form">
              {/* Left Column */}
              <Col lg={4} className="colonna-form">
                {/* Codice Contratto */}
                <Form.Group className="mb-3 px-4 responsive-px d-flex justify-content-between align-items-center">
                  <Form.Label>Codice Contratto *</Form.Label>
                  <InputText
                    disabled={!isEditing}
                    className="w-50 input-crea-edit"
                    value={contratto.codice}
                    onChange={(e) => {
                      handleChange("codice", e.target.value);
                      setErrors((prev) => ({ ...prev, codice: false }));
                    }}
                    placeholder="Inserisci codice contratto"
                    style={errors.codice ? { border: "1px solid red" } : {}}
                  />
                </Form.Group>

                {/* Committente */}
                <Form.Group className="mb-3 px-4 responsive-px d-flex justify-content-between align-items-center">
                  <Form.Label>Committente *</Form.Label>
                  <Dropdown
                    disabled={!isEditing}
                    editable
                    className="w-50 input-crea-edit p-0"
                    value={contratto.committenteId}
                    options={committenti
                      .filter((committente) =>
                        committente.ragioneSociale.toLowerCase().includes(searchText.toLowerCase())
                      )
                      .map((committente) => ({
                        label: committente.ragioneSociale,
                        value: committente.id,
                      }))}
                    onChange={(e) => {
                      handleChange("committenteId", e.value);
                      setErrors((prev) => ({ ...prev, committenteId: false }));
                    }}
                    onInput={(e) => {
                      const inputValue = (e.target as HTMLInputElement).value;
                      setSearchText(inputValue);
                    }}
                    placeholder="Seleziona Committente"
                  />
                </Form.Group>

                {/* Data Inizio */}
                <Form.Group className="mb-3 px-4 responsive-px d-flex justify-content-between align-items-center">
                  <Form.Label>Data Inizio *</Form.Label>
                  <div className="w-50">
                    {/* Wrapper div con stile condizionale */}
                    <div>
                      <DatePicker
                        disabled={!isEditing}
                        className="w-100 input-crea-edit cursor-pointer"
                        locale="it"
                        selected={contratto.dataInizio ? new Date(contratto.dataInizio) : null}
                        onChange={(date) => {
                          handleChange("dataInizio", date ? dateToString(date) : "");
                          setErrors((prev) => ({ ...prev, dataInizio: false }));
                        }}
                        placeholderText="Seleziona data inizio"
                        dateFormat="dd/MM/yyyy"
                      />
                    </div>
                  </div>
                </Form.Group>

                {/* Data Fine */}
                <Form.Group className="mb-3 px-4 responsive-px d-flex justify-content-between align-items-center">
                  <Form.Label>Data Fine</Form.Label>
                  <div className="w-50">
                    <DatePicker
                      disabled={!isEditing}
                      className="w-100 input-crea-edit cursor-pointer"
                      locale="it"
                      selected={contratto.dataFine ? new Date(contratto.dataFine) : null}
                      minDate={contratto.dataInizio ? new Date(contratto.dataInizio) : undefined}
                      onChange={(date) => handleChange("dataFine", date ? dateToString(date) : "")}
                      placeholderText="Seleziona data fine"
                      dateFormat="dd/MM/yyyy"
                    />
                  </div>
                </Form.Group>

                {/* Importo */}
                <Form.Group className="mb-3 px-4 responsive-px d-flex justify-content-between align-items-center">
                  <Form.Label>Importo</Form.Label>
                  <InputNumber
                    disabled={!isEditing}
                    className="w-50 input-crea-edit"
                    inputId="currency-germany"
                    value={contratto.importo === 0 ? null : contratto.importo}
                    onChange={(e) => handleChange("importo", parseFloat(e.value?.toString() || "0"))}
                    mode="currency"
                    currency="EUR"
                    locale="it-IT"
                    placeholder="0,00€"
                  />
                </Form.Group>

                {/* Sconto come percentuale */}
                <Form.Group className="mb-3 px-4 responsive-px d-flex justify-content-between align-items-center">
                  <Form.Label>Sconto</Form.Label>
                  <InputNumber
                    disabled={!isEditing}
                    className="w-50 input-crea-edit"
                    inputId="percentage-sconto"
                    value={contratto.sconto === 0 ? null : contratto.sconto}
                    onChange={(e) => handleChange("sconto", parseFloat(e.value?.toString() || "0"))}
                    mode="decimal"
                    suffix="%"
                    placeholder="0,00%"
                  />
                </Form.Group>

                {/* Documento Principale */}
                {action === "modifica" && (
                  <Form.Group className="mb-3 px-4 responsive-px d-flex justify-content-between align-items-center">
                    <Form.Label style={{ margin: "0" }}>Documento Principale</Form.Label>
                    {principalFileName ? (
                      <Button
                        className="w-50 button"
                        onClick={() => {
                          if (principalFileUrl) {
                            window.open(principalFileUrl, "_blank");
                          }
                        }}
                      >
                        {principalFileName || "Nessun file principale"}
                      </Button>
                    ) : (
                      <InputText disabled className="w-50 input-crea-edit" value={"Nessun documento principale"} />
                    )}
                  </Form.Group>
                )}
              </Col>

              {/* Center Column */}
              <Col lg={4} className="colonna-centrale colonna-form">
                {/* Metodo di Pagamento */}
                <Form.Group className="mb-3 px-4 responsive-px d-flex justify-content-between align-items-center">
                  <Form.Label>Metodo di Pagamento</Form.Label>
                  <Dropdown
                    disabled={!isEditing}
                    editable
                    className="w-50 input-crea-edit p-0"
                    value={contratto.metodoPagamento}
                    options={metodiDiPagamento
                      .filter((metodo) =>
                        metodo.metodoPagamento.toLowerCase().includes(paymentSearchText.toLowerCase())
                      )
                      .map((metodo) => ({
                        label: metodo.metodoPagamento,
                        value: metodo.metodoPagamento,
                      }))}
                    onChange={(e) => handleChange("metodoPagamento", e.value)}
                    onInput={(e) => {
                      const inputValue = (e.target as HTMLInputElement).value;
                      setPaymentSearchText(inputValue);
                    }}
                    placeholder="Seleziona Metodo di Pagamento"
                  />
                </Form.Group>

                {/* Emissione Polizze */}
                <Form.Group className="mb-3 px-4 responsive-px d-flex justify-content-between align-items-center">
                  <Form.Label>Emissione Polizze</Form.Label>
                  <div className="w-50">
                    <DatePicker
                      disabled={!isEditing}
                      className="w-100 input-crea-edit cursor-pointer"
                      locale="it"
                      selected={contratto.emissionePolizze ? new Date(contratto.emissionePolizze) : null}
                      onChange={(date) => handleChange("emissionePolizze", date ? dateToString(date) : "")}
                      placeholderText="Seleziona data emissione"
                      dateFormat="dd/MM/yyyy"
                    />
                  </div>
                </Form.Group>

                {/* Scadenza Polizze */}
                <Form.Group className="mb-3 px-4 responsive-px d-flex justify-content-between align-items-center">
                  <Form.Label>Scadenza Polizze</Form.Label>
                  <div className="w-50">
                    <DatePicker
                      disabled={!isEditing}
                      className="w-100 input-crea-edit cursor-pointer"
                      locale="it"
                      selected={contratto.scadenzaPolizze ? new Date(contratto.scadenzaPolizze) : null}
                      onChange={(date) => handleChange("scadenzaPolizze", date ? dateToString(date) : "")}
                      placeholderText="Seleziona data scadenza"
                      dateFormat="dd/MM/yyyy"
                    />
                  </div>
                </Form.Group>

                {/* Acquisizione Ati */}
                <Form.Group className="mb-3 px-4 responsive-px d-flex justify-content-between align-items-center">
                  <Form.Label>Acquisizione Ati</Form.Label>
                  <Form.Check
                    disabled={!isEditing}
                    type="switch"
                    id="acquisizioneAti-switch"
                    label=""
                    checked={contratto.acquisizioneAti}
                    onChange={(e) => handleChange("acquisizioneAti", e.target.checked)}
                    style={{ transform: "scale(1.2)" }}
                  />
                </Form.Group>

                {/* Campi visibili solo se acquisizioneAti = true */}
                {contratto.acquisizioneAti && (
                  <>
                    {/* Nome Azienda */}
                    <Form.Group className="mb-3 px-4 responsive-px d-flex justify-content-between align-items-center">
                      <Form.Label>Nome Azienda</Form.Label>
                      <InputText
                        disabled={!isEditing}
                        className="w-50 input-crea-edit"
                        value={contratto.nomeAzienda}
                        onChange={(e) => handleChange("nomeAzienda", e.target.value)}
                        placeholder="Inserisci nome azienda"
                      />
                    </Form.Group>

                    {/* Percentuale Acquisizione Ati */}
                    <Form.Group className="mb-3 px-4 responsive-px d-flex justify-content-between align-items-center">
                      <Form.Label>Percentuale Acquisizione Ati</Form.Label>
                      <InputText
                        disabled={!isEditing}
                        className="w-50 input-crea-edit"
                        value={contratto.percentualeAcquisizioneAti.toString()}
                        onChange={(e) => handleChange("percentualeAcquisizioneAti", parseInt(e.target.value) || 0)}
                        placeholder="Inserisci percentuale acquisizione Ati"
                      />
                    </Form.Group>
                  </>
                )}
              </Col>

              {/* Right Column */}
              <Col lg={4} className="colonna-form">
                {/* Note (descrizione) */}
                <Form.Group className="mb-3 px-4 responsive-px">
                  <InputTextarea
                    disabled={!isEditing}
                    className="input-crea-edit w-100"
                    value={contratto.descrizione}
                    onChange={(e) => handleChange("descrizione", e.target.value)}
                    rows={5}
                    placeholder="Inserisci note"
                  />
                </Form.Group>

                {/* Atto Aggiuntivo */}
                <Form.Group className="mb-3 px-4 responsive-px d-flex justify-content-between align-items-center">
                  <Form.Label>Atto Aggiuntivo</Form.Label>
                  <Form.Check
                    disabled={!isEditing}
                    type="switch"
                    id="attoAggiuntivo-switch"
                    label=""
                    checked={contratto.attoAggiuntivo}
                    onChange={(e) => handleChange("attoAggiuntivo", e.target.checked)}
                    style={{ transform: "scale(1.2)" }}
                  />
                </Form.Group>

                {/* Campi visibili solo se attoAggiuntivo = true */}
                {contratto.attoAggiuntivo && (
                  <>
                    {/* Data Atto Aggiuntivo */}
                    <Form.Group className="mb-3 px-4 responsive-px d-flex justify-content-between align-items-center">
                      <Form.Label>Data Atto Aggiuntivo</Form.Label>
                      <div className="w-50">
                        <DatePicker
                          disabled={!isEditing}
                          className="w-100 input-crea-edit cursor-pointer"
                          locale="it"
                          selected={contratto.dataAttoAggiuntivo ? new Date(contratto.dataAttoAggiuntivo) : null}
                          onChange={(date) => handleChange("dataAttoAggiuntivo", date ? dateToString(date) : "")}
                          placeholderText="Seleziona data atto aggiuntivo"
                          dateFormat="dd/MM/yyyy"
                        />
                      </div>
                    </Form.Group>

                    {/* Scadenza Atto Aggiuntivo */}
                    <Form.Group className="mb-3 px-4 responsive-px d-flex justify-content-between align-items-center">
                      <Form.Label>Scadenza Atto Aggiuntivo</Form.Label>
                      <div className="w-50">
                        <DatePicker
                          disabled={!isEditing}
                          className="w-100 input-crea-edit cursor-pointer"
                          locale="it"
                          selected={
                            contratto.scadenzaAttoAggiuntivo ? new Date(contratto.scadenzaAttoAggiuntivo) : null
                          }
                          onChange={(date) => handleChange("scadenzaAttoAggiuntivo", date ? dateToString(date) : "")}
                          placeholderText="Seleziona scadenza atto aggiuntivo"
                          dateFormat="dd/MM/yyyy"
                        />
                      </div>
                    </Form.Group>

                    {/* Importo Atto Aggiuntivo */}
                    <Form.Group className="mb-3 px-4 responsive-px4 d-flex justify-content-between align-items-center">
                      <Form.Label>Importo Atto Aggiuntivo</Form.Label>
                      <InputNumber
                        disabled={!isEditing}
                        className="w-50 input-crea-edit"
                        inputId="currency-germany"
                        value={contratto.importoAttoAggiuntivo === 0 ? null : contratto.importoAttoAggiuntivo}
                        onChange={(e) => handleChange("importoAttoAggiuntivo", parseFloat(e.value?.toString() || "0"))}
                        mode="currency"
                        currency="EUR"
                        locale="it-IT"
                        placeholder="0,00€"
                      />
                    </Form.Group>
                  </>
                )}
              </Col>
            </Row>
          </div>
        </Col>

        {/* File Upload Section */}
        <Col lg={5} className="ps-0">
          <Container className="create-edit-under">
            {action === "modifica" && !isLoadingPrincipalFile ? (
              <FileUploadComponent
                folderType="contratti"
                folderKey={contratto.codice}
                elementContratto={contratto}
                onFilesLoaded={(files) => setFilesContratto(files)}
                onPrincipalFileUpdated={() => {
                  if (id) {
                    const url = `${urlcontratti}/${id}`;
                    dispatch(allGet(url, token)).then((updatedContratto) => {
                      if (updatedContratto) {
                        setContratto(updatedContratto);
                      }
                    });
                  }
                }}
              />
            ) : (
              <>
                <h5 className="text-start m-0">File</h5>
                <br />
                <p>Per caricare un file, devi prima aver creato il contratto</p>
              </>
            )}
          </Container>
        </Col>

        {/* Referenti Section */}
        <Col lg={7} className="pe-0">
          <div className="create-edit-under div-flex-bet">
            <Container className="d-flex flex-column">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="text-start m-0">
                  Referenti :
                  {arrayReferentiCommittente.length > 0 ? (
                    <span style={{ fontSize: "20px", fontWeight: "400" }}>
                      {" "}
                      seleziona i referenti da associare al cliente!
                    </span>
                  ) : (
                    <span style={{ fontSize: "20px", fontWeight: "400" }}>
                      {" "}
                      non ci sono referenti associati a questo cliente!
                    </span>
                  )}
                </h5>
              </div>
              <div className="referenti-list">
                {arrayReferentiCommittente.map((ref, index) => (
                  <div key={index} className="referente-item" onClick={() => toggleReferente(ref)}>
                    <h6>
                      <b>{`${ref.nome} ${ref.cognome}`}</b>
                    </h6>
                    <div className="d-flex justify-content-between mb-3">
                      <div className="referente-info">
                        <img src="../../assets/IconMail.svg" alt="Mail Icon" className="svg-icon" />
                        <span>{ref.email}</span>
                      </div>
                      <div className="referente-info">
                        <img src="../../assets/IconPhone.svg" alt="Phone Icon" className="svg-icon" />
                        <span>{ref.telefono}</span>
                      </div>
                      <div className="referente-info">
                        <img src="../../assets/IconRuolo.svg" alt="Role Icon" className="svg-icon" />
                        <span>{ref.ruolo}</span>
                      </div>
                      <div className="check-container">
                        <Checkbox
                          disabled={isEditing}
                          checked={contratto.referenti.some((selectedRef) => selectedRef.id === ref.id)}
                          onChange={() => {
                            if (isEditing) {
                              toggleReferente(ref);
                            }
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Container>
          </div>
        </Col>
      </Row>

      <DeleteModal
        type="contratto"
        idObj={contratto.id}
        matchIdentifier={contratto.codice}
        show={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        onDeleteSuccess={() => {
          handleClose();
        }}
      />
    </div>
  );
};

export default FormContratti;
